import { css } from 'aphrodite';
import * as React from 'react';
import type { CoachingAdherenceScoreboard } from '../../../queries/AIConversation/useCoachingAdherenceScoreboardQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import {
	type CoachingAdherenceRepScore,
	MIN_CALLS_FOR_SHARE,
	followedShare,
	formatPercent,
	partlyOrBetterShare,
	shareTier,
} from '../../constants/coachingAdherence';
import {
	type CoachStage,
	formatPerDemo,
	isAutoMode,
	isNeverFollower,
	repActivityRates,
	sendsRepDetails,
	stageCounts,
	tierPillColor,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { ScoreboardRepCalls } from './ScoreboardRepCalls';

const STAGE_COLUMNS: Array<[CoachStage, string]> = [
	['hook', 'Hook used'],
	['opener', 'Opener used'],
	['ask', 'Ask made'],
];

function StageCell({ rep, stage }: { rep: CoachingAdherenceRepScore; stage: CoachStage }) {
	const { used, counted } = stageCounts(rep, stage);
	if (counted === 0) {
		return <span className={css(styleSheet.faded)}>&ndash;</span>;
	}
	return (
		<span title={`${used} of ${counted} calls`}>
			{used}/{counted}
		</span>
	);
}

function PerDemoCell({ value, title }: { value: number | null; title: string }) {
	return (
		<span className={value == null ? css(styleSheet.faded) : undefined} title={title}>
			{formatPerDemo(value)}
		</span>
	);
}

interface IProps {
	scoreboard: CoachingAdherenceScoreboard;
	reps: CoachingAdherenceRepScore[];
	initialExpandedRepId?: string | null;
	/** The range as requested, not the server's echo, so the drill-down matches the API call exactly. */
	range: { start: string; end: string };
	/** Show each rep's cohort under their name: on a board that mixes cohorts. */
	showCohort?: boolean;
	onSelectCall: (aiConversationId: string) => void;
}

export function ScoreboardTable({
	scoreboard,
	reps,
	initialExpandedRepId = null,
	range,
	showCohort = false,
	onSelectCall,
}: IProps) {
	const [expandedRepId, setExpandedRepId] = React.useState<string | null>(initialExpandedRepId);
	const { followedScore } = scoreboard;
	// An API that predates the dialing totals sends none, so the columns stay off rather than reading zero.
	const showActivity = sendsRepDetails(reps);
	const columnCount = 10 + (showActivity ? 3 : 0);

	return (
		<div className={css(styleSheet.tableWrap)}>
			<table className={css(styleSheet.table)}>
				<thead>
					<tr>
						<th className={css(styleSheet.th, styleSheet.alignLeft)}>Rep</th>
						<th className={css(styleSheet.th)}>Calls past hook</th>
						<th className={css(styleSheet.th)}>Followed ({followedScore}+)</th>
						<th className={css(styleSheet.th)}>Share followed</th>
						<th className={css(styleSheet.th)}>Share partly ({followedScore / 2}+)</th>
						<th className={css(styleSheet.th)}>Hook-rejected</th>
						<th className={css(styleSheet.th)}>Booked</th>
						{showActivity ? (
							<>
								<th className={css(styleSheet.th)} title='Every dial, confirmation and reschedule dials included'>
									Dials
								</th>
								<th className={css(styleSheet.th)} title='Prospecting dials (cold demo dials included) per demo booked'>
									Dials/demo
								</th>
								<th className={css(styleSheet.th)} title='Connects per demo booked'>
									Connects/demo
								</th>
							</>
						) : null}
						{STAGE_COLUMNS.map(([stage, label]) => (
							<th key={stage} className={css(styleSheet.th)}>
								{label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{reps.map(rep => {
						const expanded = expandedRepId === rep.userId;
						const share = followedShare(rep);
						const partly = partlyOrBetterShare(rep);
						const thin = rep.pastHookCalls < MIN_CALLS_FOR_SHARE;
						const repName = rep.name || rep.userId;
						const activity = repActivityRates(rep);
						return (
							<React.Fragment key={rep.userId}>
								<tr>
									<td className={css(styleSheet.td, styleSheet.alignLeft)}>
										<div className={css(styleSheet.repCell)}>
											<Button
												variant='ghost'
												size='sm'
												styles={[styleSheet.repToggle]}
												aria-expanded={expanded}
												onClick={() => setExpandedRepId(expanded ? null : rep.userId)}
											>
												{repName}
											</Button>
											{isAutoMode(rep) ? (
												<span
													className={css(styleSheet.autoTag)}
													title='Gets coach cards on their own, without clicking Get suggestion'
												>
													<Pill variant='tag' size='small' color='blue' label='Auto' />
												</span>
											) : null}
											{showCohort ? (
												<span className={css(styleSheet.repCohort)}>{rep.cohort ?? 'No cohort'}</span>
											) : null}
											{isNeverFollower(rep) ? (
												<span className={css(styleSheet.neverFlag)}>never followed the coach</span>
											) : null}
										</div>
									</td>
									<td className={css(styleSheet.td)}>{rep.pastHookCalls}</td>
									<td className={css(styleSheet.td)}>{rep.followedCalls}</td>
									<td className={css(styleSheet.td)}>
										<span
											className={css(styleSheet.pillCell, thin ? styleSheet.faded : null)}
											title={thin ? `Fewer than ${MIN_CALLS_FOR_SHARE} calls past the hook` : undefined}
										>
											<Pill
												variant='tag'
												size='small'
												color={tierPillColor(shareTier(share))}
												label={formatPercent(share)}
											/>
										</span>
									</td>
									<td className={css(styleSheet.td)}>
										<span className={css(styleSheet.pillCell, thin ? styleSheet.faded : null)}>
											<Pill
												variant='tag'
												size='small'
												color={partly == null ? 'gray' : 'yellow'}
												label={formatPercent(partly)}
											/>
										</span>
									</td>
									<td className={css(styleSheet.td)}>{rep.hookRejectedCalls}</td>
									<td className={css(styleSheet.td)}>{rep.bookedCalls}</td>
									{showActivity ? (
										<>
											<td className={css(styleSheet.td)}>
												{activity ? activity.allDials : <span className={css(styleSheet.faded)}>&ndash;</span>}
											</td>
											<td className={css(styleSheet.td)}>
												<PerDemoCell
													value={activity?.dialsPerDemo ?? null}
													title={
														activity
															? `${activity.dials} dials, ${activity.demos} demos`
															: 'No dialing totals for this rep'
													}
												/>
											</td>
											<td className={css(styleSheet.td)}>
												<PerDemoCell
													value={activity?.connectsPerDemo ?? null}
													title={
														activity
															? `${activity.connects} connects, ${activity.demos} demos`
															: 'No dialing totals for this rep'
													}
												/>
											</td>
										</>
									) : null}
									{STAGE_COLUMNS.map(([stage]) => (
										<td key={stage} className={css(styleSheet.td)}>
											<StageCell rep={rep} stage={stage} />
										</td>
									))}
								</tr>
								{expanded ? (
									<tr>
										<td className={css(styleSheet.expandedCell)} colSpan={columnCount}>
											<ScoreboardRepCalls
												userId={rep.userId}
												repName={repName}
												start={range.start}
												end={range.end}
												followedScore={followedScore}
												onSelectCall={onSelectCall}
											/>
										</td>
									</tr>
								) : null}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
